import React, { useRef, useEffect, useState, useCallback } from 'react';

interface GuessInputProps {
  disabled?: boolean;
  onSubmit: (guess: string) => Promise<void>;
}

const GuessInput: React.FC<GuessInputProps> = ({ disabled, onSubmit }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [guess, setGuess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const focusInput = useCallback(() => {
    setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  useEffect(() => {
    if (!disabled) {
      focusInput();
    }
  }, [disabled, focusInput]);

  // Re-grab focus when user clicks anywhere on the page
  useEffect(() => {
    const handleClick = () => {
      if (!disabled && !isSubmitting) {
        focusInput();
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [disabled, isSubmitting, focusInput]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting) return;
    setError(null);
    setIsSubmitting(true);

    try {
      await onSubmit(guess);
      setGuess('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit guess');
    } finally {
      setIsSubmitting(false);
      focusInput();
    }
  };

  return (
    <form className="guess-input" onSubmit={handleSubmit}>
      <input
        ref={inputRef}
        value={guess}
        maxLength={5}
        onChange={(e) => setGuess(e.target.value.replace(/[^a-zA-Z]/g, '').toLowerCase())}
        placeholder="TYPE YOUR GUESS"
        disabled={disabled || isSubmitting}
        aria-label="Guess"
        autoFocus
        className="guess-input-field"
      />
      <button type="submit" disabled={disabled || isSubmitting || guess.length !== 5}>
        {isSubmitting ? 'Checking...' : 'Submit'}
      </button>
      {error && <div className="notice error">{error}</div>}
    </form>
  );
};

export default GuessInput;
